import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import Spinner from 'components/spinner/Spinner';
import InfiniteScrollIndicator from 'components/infinite-load-indicator/InfiniteScrollIndicator';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import { loadRecentMedia } from 'modules/proposal/reducers/creator-recent-media';

import { __t, applyTranslations } from 'utils/language';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK } from 'utils/channels';

import type { ProposalImage, RecentPost } from 'types';
import type { ConsentReducerConsentsMap } from 'redux-store/types/ConsentReducer.type';

import './recent-media-grid.scss';

applyTranslations(require('./recent-media-grid.i18n.json'));

type OwnProps = {
  profileId: number,
  channelId: number,
  isTikTok?: boolean,
  lastPickedElement: string | undefined | null,
  alreadyPickedPosts: Array<RecentPost>,
  onImageClick: (a: ProposalImage) => void,
  onCloseModal: () => void
};

type StateProps = {
  posts: Array<RecentPost>,
  isLoading: boolean,
  isOnLastPage: boolean,
  currentlyLoadedPage: number,
  userChannelConsents: ConsentReducerConsentsMap
};

type DispatchProps = {
  dispatchLoadRecentMedia: (b: number, a: number, page?: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  canLoadOnScroll: boolean
};

type PostImageWithFallbackProps = {
  post: RecentPost,
  hasConsent: boolean,
  channel: 'instagram' | 'tiktok',
  lastPickedElement: string | undefined | null,
  isThisPostAlreadyPicked: boolean,
  onClick: () => void
};

const PostImageWithFallback = (
  {
    post,
    hasConsent,
    channel,
    onClick,
    isThisPostAlreadyPicked,
    lastPickedElement
  }: PostImageWithFallbackProps
) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isFailed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;

    if (lastPickedElement === post.url && element) {
      element.scrollIntoView({ block: 'center' });
    }
  }, [post.url]);

  if (isFailed) {
    return (
      <span className="recent-media-grid__failed-container">
        {__t('recent_media_grid.failed_image')}
      </span>
    );
  }

  const classNames = classnames('recent-media-grid__image-container', {
    'recent-media-grid__image-container--with-hover': hasConsent,
    'recent-media-grid__image-container--picked': isThisPostAlreadyPicked
  });

  return (
    <div
      ref={ref}
      className={classNames}
    >
      {hasConsent && (
        <img
          onClick={onClick}
          onError={() => setFailed(true)}
          data-src={post.assetUrl}
          className="lazyload recent-media-grid__image"
          alt=""
        />
      )}
      {!hasConsent && (
        <ConsentPlaceholder
          withLearnMore={!isThisPostAlreadyPicked}
          type={channel}
          link={post.url}
          onClick={onClick}
        />
      )}
    </div>
  );
};

class RecentMediaGrid extends React.Component<Props, State> {
  static defaultProps = {
    posts: [],
    alreadyPickedPosts: []
  };

  state = {
    canLoadOnScroll: false
  };

  componentDidMount() {
    const {
      profileId,
      channelId,
      dispatchLoadRecentMedia
    } = this.props;

    if (profileId) {
      dispatchLoadRecentMedia(profileId, channelId)
        .then(() => this.setState({
          canLoadOnScroll: true
        }));
    }
  }

  handleLoadNextPage = () => {
    const {
      profileId,
      channelId,
      dispatchLoadRecentMedia,
      currentlyLoadedPage
    } = this.props;

    dispatchLoadRecentMedia(profileId, channelId, currentlyLoadedPage + 1);
  };

  renderPost = (post: RecentPost) => {
    const {
      alreadyPickedPosts,
      onImageClick,
      onCloseModal,
      lastPickedElement,
      userChannelConsents,
      isTikTok
    } = this.props;

    const {
      imageUrl,
      url
    } = post;

    const isThisPostAlreadyPicked = Boolean(alreadyPickedPosts.find(pickedPost => pickedPost.url === url));
    const channel = isTikTok ? CHANNEL_TYPE_TIKTOK : CHANNEL_TYPE_INSTAGRAM;
    const hasConsent = userChannelConsents[channel];

    return (
      <PostImageWithFallback
        hasConsent={hasConsent}
        channel={channel}
        lastPickedElement={lastPickedElement}
        isThisPostAlreadyPicked={isThisPostAlreadyPicked}
        key={`image-${imageUrl}`}
        post={post}
        onClick={() => {
          onImageClick({ ...post });
          onCloseModal();
        }}
      />
    );
  };

  render() {
    const {
      isLoading,
      isOnLastPage,
      isTikTok,
      currentlyLoadedPage,
      posts
    } = this.props;

    const { canLoadOnScroll } = this.state;

    const classNames = classnames('recent-media-grid', {
      'recent-media-grid--tiktok': isTikTok
    });

    return (
      <section className={classNames}>
        <div className="recent-media-grid__content">
          {currentlyLoadedPage < 1 && isLoading && (
            <Spinner
              position="cover"
              size="small"
              color="light"
            />
          )}
          {posts.map(this.renderPost)}
        </div>
        {canLoadOnScroll && !isOnLastPage && (
          <InfiniteScrollIndicator
            isLoading={isLoading}
            onLoadMore={this.handleLoadNextPage}
          />
        )}
      </section>
    );
  }
}

const mapStateToProps = ({ creatorRecentMedia, consentReducer }): StateProps => ({
  posts: creatorRecentMedia.posts,
  isOnLastPage: creatorRecentMedia.isOnLastPage,
  isLoading: creatorRecentMedia.loadingStatus,
  currentlyLoadedPage: creatorRecentMedia.currentlyLoadedPage,
  userChannelConsents: consentReducer.consents
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchLoadRecentMedia: (profileId: number, channelId: number, page?: number): Promise<void> => dispatch(loadRecentMedia(profileId, channelId, page))
});


export default connect(mapStateToProps, mapDispatchToProps)(RecentMediaGrid);
